import React, { useState, useEffect } from 'react';
import LoginModal from '../components/auth/LoginModal';
import RegisterModal from '../components/auth/RegisterModal';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';

const LandingPage = () => {
  const [activeModal, setActiveModal] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openModal = (modalType) => {
    setActiveModal(modalType);
  };

  const closeModal = () => {
    setActiveModal(null);
  };

  return (
    <div className="landing-page">
      {/* Enhanced Navigation */}
      <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
        <div className="nav-container">
          <div className="nav-brand">
            <div className="university-logo">
              <div className="logo-circle">
                <img
                  src="/images/logo.png"
                  alt="OOU Logo"
                  className="nav-logo-image"
                  onError={(e) => {
                    // Fallback to different image formats if the first one fails
                    if (e.target.src.includes('logo.png')) {
                      e.target.src = '/images/logo.jpg';
                    } else if (e.target.src.includes('logo.jpg')) {
                      e.target.src = '/images/logo.svg';
                    } else {
                      // If all image formats fail, show fallback text
                      e.target.style.display = 'none';
                      e.target.nextElementSibling.style.display = 'flex';
                    }
                  }}
                />
                <span className="logo-text" style={{ display: 'none' }}>OU</span>
              </div>
              <div className="brand-text">
                <h3>EPROFICIENCY</h3>
                <p>English Proficiency System</p>
              </div>
            </div>
          </div>
          <ul className="nav-menu">
            <li><a href="#home" className="nav-link">Home</a></li>
            <li><a href="#about" className="nav-link">About</a></li>
            <li><a href="#features" className="nav-link">Features</a></li>
            <li><a href="#contact" className="nav-link">Contact</a></li>
            <li>
              <button
                onClick={() => openModal('login')}
                className="nav-btn nav-btn-login"
              >
                Login
              </button>
            </li>
            <li>
              <button
                onClick={() => openModal('register')}
                className="nav-btn nav-btn-register"
              >
                Get Started
              </button>
            </li>
            <li>
              <a href="/track-payment"
                className="nav-btn nav-btn-register"
              >
                Track Payments
              </a>
            </li>
          </ul>
        </div>
      </nav>

      {/* Hero Section with Gradient Background */}
      <section id="home" className="hero-section">
        <div className="hero-background">
          <div className="gradient-overlay"></div>
          <div className="hero-pattern"></div>
        </div>
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-icon">🏛️</span>
              <span>Official University Portal</span>
            </div>
            <h1 className="hero-title">
              <span className="title-main">English Proficiency</span>
              <span className="title-sub">Made Simple & Secure</span>
            </h1>
            <p className="hero-description">
              Effortlessly request and retrieve your official English-proficiency letter from Olabisi Onabanjo University—secure, swift, and accepted by institutions worldwide.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">50,000+</span>
                <span className="stat-label">Certificates Verified</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">99.9%</span>
                <span className="stat-label">Accuracy Rate</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Available</span>
              </div>
            </div>
            <div className="hero-actions">
              <button
                onClick={() => openModal('register')}
                className="hero-btn hero-btn-primary"
              >
                <span className="btn-icon">🚀</span>
                Start Verification
              </button>
              <button
                onClick={() => openModal('login')}
                className="hero-btn hero-btn-secondary"
              >
                <span className="btn-icon">🔍</span>
                Verify Now
              </button>
            </div>
            <div className="hero-trust">
              <p>Trusted by employers and institutions globally</p>
              <div className="trust-badges">
                <span className="trust-badge">🔒 SSL Secured</span>
                <span className="trust-badge">✅ ISO Certified</span>
                <span className="trust-badge">🏆 Award Winning</span>
              </div>
            </div>
          </div>
          <div className="hero-visual">
            <div className="certificate-mockup">
              <div className="certificate-frame">
                <div className="certificate-header">
                  <div className="cert-logo">
                    <img
                      src="/images/logo.png"
                      alt="OOU Logo"
                      className="cert-logo-image"
                      onError={(e) => {
                        // Fallback to different image formats if the first one fails
                        if (e.target.src.includes('logo.png')) {
                          e.target.src = '/images/logo.jpg';
                        } else if (e.target.src.includes('logo.jpg')) {
                          e.target.src = '/images/logo.svg';
                        } else {
                          // If all image formats fail, show fallback text
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }
                      }}
                    />
                    <span className="cert-logo-fallback" style={{ display: 'none' }}>OU</span>
                  </div>
                  <div className="cert-title">
                    <h4>OLABISI ONABANJO UNIVERSITY</h4>
                    <p>Certificate of Graduation</p>
                  </div>
                </div>
                <div className="certificate-body">
                  <div className="cert-line"></div>
                  <div className="cert-line short"></div>
                  <div className="cert-line"></div>
                  <div className="cert-signature">
                    <div className="signature-line"></div>
                    <div className="signature-line"></div>
                  </div>
                </div>
                <div className="verification-badge">
                  <span className="badge-check">✓</span>
                  <span>Verified</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Features Section */}
      <section id="features" className="features-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">⭐</span>
              <span>Why Choose Us</span>
            </div>
            <h2 className="section-title">Advanced Verification Features</h2>
            <p className="section-description">
              Our cutting-edge technology ensures the highest level of security and reliability
              for all your certificate verification needs.
            </p>
          </div>

          <div className="features-grid">
            <FeatureCard
              icon="🛡️"
              title="Bank-Level Security"
              description="256-bit SSL encryption and multi-factor authentication protect your sensitive data with the same security used by major financial institutions."
              color="blue"
              delay="0.1s"
            />
            <FeatureCard
              icon="⚡"
              title="Instant Verification"
              description="Get real-time verification results in seconds with our advanced AI-powered verification engine and cloud infrastructure."
              color="yellow"
              delay="0.2s"
            />
            <FeatureCard
              icon="📊"
              title="Detailed Analytics"
              description="Comprehensive verification reports with detailed analytics, audit trails, and exportable data for institutional records."
              color="green"
              delay="0.3s"
            />
            <FeatureCard
              icon="🌍"
              title="Global Access"
              description="Access our verification system from anywhere in the world, 24/7, with support for multiple languages and currencies."
              color="purple"
              delay="0.4s"
            />
            <FeatureCard
              icon="💳"
              title="Secure Payments"
              description="Multiple payment options with PCI-compliant processing, including credit cards, digital wallets, and bank transfers."
              color="red"
              delay="0.5s"
            />
            <FeatureCard
              icon="📱"
              title="Mobile Optimized"
              description="Fully responsive design with native mobile app experience, optimized for smartphones and tablets."
              color="indigo"
              delay="0.6s"
            />
          </div>
        </div>
      </section>

      {/* About Section with Timeline */}
      <section id="about" className="about-section">
        <div className="section-container">
          <div className="about-grid">
            <div className="about-content">
              <div className="section-badge">
                <span className="badge-icon">🏛️</span>
                <span>Our Legacy</span>
              </div>
              <h2 className="section-title">About Olabisi Onabanjo University</h2>
              <p className="about-text">
                Established in 1982, Olabisi Onabanjo University stands as a beacon of
                academic excellence in Nigeria. Our institution has been at the forefront
                of innovation in education, research, and community development for over
                four decades.
              </p>
              <p className="about-text">
                Our state-of-the-art certificate verification system represents our
                commitment to maintaining the highest standards of academic integrity
                and protecting the value of our graduates' achievements.
              </p>

              <div className="achievement-grid">
                <div className="achievement-item">
                  <div className="achievement-icon">🎓</div>
                  <div className="achievement-content">
                    <h4>50,000+</h4>
                    <p>Graduates Worldwide</p>
                  </div>
                </div>
                <div className="achievement-item">
                  <div className="achievement-icon">🏆</div>
                  <div className="achievement-content">
                    <h4>12</h4>
                    <p>Academic Faculties</p>
                  </div>
                </div>
                <div className="achievement-item">
                  <div className="achievement-icon">📚</div>
                  <div className="achievement-content">
                    <h4>100+</h4>
                    <p>Degree Programs</p>
                  </div>
                </div>
                <div className="achievement-item">
                  <div className="achievement-icon">🌟</div>
                  <div className="achievement-content">
                    <h4>40+</h4>
                    <p>Years of Excellence</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="about-visual">
              <div className="university-showcase">
                <div className="showcase-card primary">
                  <div className="card-icon">🎓</div>
                  <h3>Academic Excellence</h3>
                  <p>Recognized for outstanding educational programs and research initiatives</p>
                </div>
                <div className="showcase-card secondary">
                  <div className="card-icon">🔬</div>
                  <h3>Research Innovation</h3>
                  <p>Leading groundbreaking research in science, technology, and humanities</p>
                </div>
                <div className="showcase-card tertiary">
                  <div className="card-icon">🤝</div>
                  <h3>Global Partnerships</h3>
                  <p>Collaborating with institutions worldwide for academic exchange</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Process Section */}
      <section className="process-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">⚙️</span>
              <span>Simple Process</span>
            </div>
            <h2 className="section-title">How English Proficiency Collection Works</h2>
            <p className="section-description">
              Our streamlined process makes english proficiency collection quick, secure, and reliable.
            </p>
          </div>

          <div className="process-flow">
            <ProcessStep
              number="01"
              title="Create Account"
              description="Register with your details and verify your email address for secure access to our platform."
              icon="👤"
              color="blue"
            />
            <ProcessStep
              number="02"
              title="Enter Certificate Details"
              description="Input the certificate number and verify all details before proceeding to payment."
              icon="📝"
              color="green"
            />
            <ProcessStep
              number="03"
              title="Secure Payment"
              description="Complete payment using our secure, encrypted payment gateway with multiple options."
              icon="💳"
              color="purple"
            />
            <ProcessStep
              number="04"
              title="Get Results"
              description="Receive instant verification results with detailed certificate information and download reports."
              icon="✅"
              color="orange"
            />
          </div>
        </div>
      </section>

      {/* Enhanced Contact Section */}
      <section id="contact" className="contact-section">
        <div className="section-container">
          <div className="section-header">
            <div className="section-badge">
              <span className="badge-icon">📞</span>
              <span>Get in Touch</span>
            </div>
            <h2 className="section-title">Contact Our Support Team</h2>
            <p className="section-description">
              Our dedicated support team is here to help with any questions about certificate verification.
            </p>
          </div>

          <div className="contact-grid">
            <ContactCard
              icon="📧"
              title="Email Support"
              details="enquiry@oouagoiwoye.edu.ng"
              description="Send us your questions and get responses within 24 hours from our expert team."
              action="Send Email"
            />
            <ContactCard
              icon="📞"
              title="Phone Support"
              details="+234 (0) 9135004446"
              description="Speak directly with our support representatives during business hours (8 AM - 5 PM WAT)."
              action="Call Now"
            />
            <ContactCard
              icon="📞"
              title="Phone Support"
              details="+234 (0) 9135004447"
              description="Speak directly with our support representatives during business hours (8 AM - 5 PM WAT)."
              action="Call Now"
            />

          </div>
        </div>
      </section>

      {/* Enhanced Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <div className="logo-circle">
                  <img
                    src="/images/logo.png"
                    alt="OOU Logo"
                    className="footer-logo-image"
                    onError={(e) => {
                      // Fallback to different image formats if the first one fails
                      if (e.target.src.includes('logo.png')) {
                        e.target.src = '/images/logo.jpg';
                      } else if (e.target.src.includes('logo.jpg')) {
                        e.target.src = '/images/logo.svg';
                      } else {
                        // If all image formats fail, show fallback text
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'flex';
                      }
                    }}
                  />
                  <span className="footer-logo-fallback" style={{ display: 'none' }}>OU</span>
                </div>
                <div className="brand-info">
                  <h3>EPROFICIENCY</h3>
                  <p>English Proficiency System</p>
                </div>
              </div>
              <p className="footer-description">
                Leading the way in academic excellence, research innovation, and community
                development. Our English Proficiency system ensures the integrity of
                our graduates' achievements worldwide.
              </p>
              <div className="social-links">
                <a href="#" className="social-link">📘</a>
                <a href="#" className="social-link">🐦</a>
                <a href="#" className="social-link">💼</a>
                <a href="#" className="social-link">📧</a>
              </div>
            </div>

            <div className="footer-links">
              <div className="link-group">
                <h4>Quick Links</h4>
                <ul>
                  <li><a href="#home">Home</a></li>
                  <li><a href="#about">About Us</a></li>
                  <li><a href="#features">Features</a></li>
                  <li><a href="#contact">Contact</a></li>
                </ul>
              </div>

              <div className="link-group">
                <h4>Services</h4>
                <ul>
                  <li><a href="#" onClick={() => openModal('register')}>English Proficiency</a></li>
                  <li><a href="#">Academic Records</a></li>
                  <li><a href="#">Transcript Services</a></li>
                  <li><a href="#">Document Authentication</a></li>
                </ul>
              </div>

              <div className="link-group">
                <h4>Support</h4>
                <ul>
                  <li><a href="#">Help Center</a></li>
                  <li><a href="#">Privacy Policy</a></li>
                  <li><a href="#">Terms of Service</a></li>
                  <li><a href="#">FAQ</a></li>
                </ul>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <p>&copy; {new Date().getFullYear()} Olabisi Onabanjo University. All rights reserved.</p>
              <div className="footer-badges">
                <span className="footer-badge">🔒 SSL Secured</span>
                <span className="footer-badge">✅ ISO Certified</span>
                <span className="footer-badge">🏆 Accredited</span>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Modals */}
      {activeModal === 'login' && (
        <LoginModal
          onClose={closeModal}
          onForgotPassword={() => openModal('forgot-password')}
        />
      )}
      {activeModal === 'register' && (
        <RegisterModal onClose={closeModal} />
      )}
      {activeModal === 'forgot-password' && (
        <ForgotPasswordModal
          onClose={closeModal}
          onBackToLogin={() => openModal('login')}
        />
      )}

      {/* Enhanced Styles */}
      <style jsx>{`
        .landing-page {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        /* Enhanced Navigation */
        .navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(30, 58, 138, 0.1);
          transition: all 0.3s ease;
          z-index: 1000;
          padding: 1rem 0;
        }

        .navbar-scrolled {
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          padding: 0.5rem 0;
        }

        .nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .university-logo {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .logo-circle {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(30, 58, 138, 0.3);
          overflow: hidden;
          position: relative;
        }

        .nav-logo-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          border-radius: 50%;
        }

        .logo-text {
          color: white;
          font-weight: 700;
          font-size: 1.2rem;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .brand-text h3 {
          color: #1e3a8a;
          font-weight: 600;
          font-size: 1.1rem;
          margin: 0;
        }

        .brand-text p {
          color: #6b7280;
          font-size: 0.8rem;
          margin: 0;
        }

        .nav-menu {
          display: flex;
          list-style: none;
          align-items: center;
          gap: 2rem;
          margin: 0;
          padding: 0;
        }

        .nav-link {
          color: #374151;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.95rem;
          transition: color 0.3s ease;
          position: relative;
        }

        .nav-link:hover {
          color: #1e3a8a;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          bottom: -5px;
          left: 0;
          width: 0;
          height: 2px;
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          transition: width 0.3s ease;
        }

        .nav-link:hover::after {
          width: 100%;
        }

        .nav-btn {
          padding: 0.6rem 1.2rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }

        .nav-btn-login {
          background: transparent;
          color: #374151;
          border: 1px solid #e5e7eb;
        }

        .nav-btn-login:hover {
          background: #f9fafb;
          color: #1e3a8a;
        }

        .nav-btn-register {
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          color: white;
          box-shadow: 0 4px 15px rgba(30, 58, 138, 0.3);
        }

        .nav-btn-register:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(30, 58, 138, 0.4);
        }

        /* Enhanced Hero Section */
        .hero-section {
          position: relative;
          min-height: 100vh;
          display: flex;
          align-items: center;
          overflow: hidden;
          padding: 6rem 0 4rem;
        }

        .hero-background {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: -1;
        }

        .gradient-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, 
            rgba(30, 58, 138, 0.05) 0%, 
            rgba(59, 130, 246, 0.05) 50%, 
            rgba(167, 243, 208, 0.05) 100%);
        }

        .hero-pattern {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: 
            radial-gradient(circle at 25% 25%, rgba(30, 58, 138, 0.1) 0%, transparent 70%),
            radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
          animation: float 20s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .hero-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }

        .hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(30, 58, 138, 0.1);
          color: #1e3a8a;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 2rem;
          animation: slideInUp 0.8s ease-out;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 1.5rem;
          animation: slideInUp 0.8s ease-out 0.2s both;
        }

        .title-main {
          display: block;
          background: linear-gradient(135deg, #1e3a8a, #3b82f6, #10b981);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .title-sub {
          display: block;
          color: #374151;
          font-weight: 600;
        }

        .hero-description {
          font-size: 1.2rem;
          color: #6b7280;
          line-height: 1.7;
          margin-bottom: 2rem;
          animation: slideInUp 0.8s ease-out 0.4s both;
        }

        .hero-stats {
          display: flex;
          gap: 2rem;
          margin-bottom: 2rem;
          animation: slideInUp 0.8s ease-out 0.6s both;
        }

        .stat-item {
          text-align: center;
        }

        .stat-number {
          display: block;
          font-size: 1.8rem;
          font-weight: 700;
          color: #1e3a8a;
        }

        .stat-label {
          font-size: 0.9rem;
          color: #6b7280;
        }

        .hero-actions {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          animation: slideInUp 0.8s ease-out 0.8s both;
        }

        .hero-btn {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 2rem;
          border-radius: 12px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          border: none;
        }

        .hero-btn-primary {
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          color: white;
          box-shadow: 0 8px 25px rgba(30, 58, 138, 0.3);
        }

        .hero-btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 35px rgba(30, 58, 138, 0.4);
        }

        .hero-btn-secondary {
          background: white;
          color: #1e3a8a;
          border: 2px solid #1e3a8a;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .hero-btn-secondary:hover {
          background: #1e3a8a;
          color: white;
          transform: translateY(-2px);
        }

        .hero-trust {
          animation: slideInUp 0.8s ease-out 1s both;
        }

        .hero-trust p {
          color: #6b7280;
          font-size: 0.9rem;
          margin-bottom: 1rem;
        }

        .trust-badges {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .trust-badge {
          background: rgba(16, 185, 129, 0.1);
          color: #059669;
          padding: 0.3rem 0.8rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        /* Certificate Mockup */
        .hero-visual {
          display: flex;
          justify-content: center;
          align-items: center;
          animation: slideInRight 1s ease-out 0.5s both;
        }

        .certificate-mockup {
          position: relative;
          transform: perspective(1000px) rotateY(-10deg) rotateX(5deg);
          transition: transform 0.3s ease;
        }

        .certificate-mockup:hover {
          transform: perspective(1000px) rotateY(-5deg) rotateX(2deg);
        }

        .certificate-frame {
          background: white;
          border-radius: 15px;
          padding: 2rem;
          box-shadow: 
            0 25px 50px rgba(0, 0, 0, 0.15),
            0 0 0 1px rgba(30, 58, 138, 0.1);
          width: 350px;
          position: relative;
        }

        .certificate-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .cert-logo {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          margin: 0 auto 1rem;
          overflow: hidden;
          position: relative;
        }

        .cert-logo-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          border-radius: 50%;
        }

        .cert-logo-fallback {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-weight: bold;
          font-size: 1rem;
        }

        .cert-title h4 {
          color: #1e3a8a;
          font-size: 1rem;
          margin-bottom: 0.5rem;
        }

        .cert-title p {
          color: #6b7280;
          font-size: 0.9rem;
        }

        .certificate-body {
          margin-bottom: 2rem;
        }

        .cert-line {
          height: 8px;
          background: linear-gradient(90deg, #e5e7eb, #d1d5db, #e5e7eb);
          border-radius: 4px;
          margin-bottom: 1rem;
        }

        .cert-line.short {
          width: 60%;
        }

        .cert-signature {
          display: flex;
          justify-content: space-between;
          margin-top: 2rem;
        }

        .signature-line {
          width: 80px;
          height: 4px;
          background: #d1d5db;
          border-radius: 2px;
        }

        .verification-badge {
          position: absolute;
          top: -10px;
          right: -10px;
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 0.5rem;
          border-radius: 50px;
          font-size: 0.8rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 0.3rem;
          box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        /* Enhanced Features Section */
        .features-section {
          padding: 6rem 0;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }

        .section-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(59, 130, 246, 0.1);
          color: #1e40af;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1rem;
        }

        .section-title {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 700;
          color: #1e3a8a;
          margin-bottom: 1rem;
        }

        .section-description {
          font-size: 1.1rem;
          color: #6b7280;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.7;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
        }

        /* About Section */
        .about-section {
          padding: 6rem 0;
          background: white;
        }

        .about-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          align-items: center;
        }

        .about-text {
          font-size: 1.1rem;
          color: #6b7280;
          line-height: 1.8;
          margin-bottom: 1.5rem;
        }

        .achievement-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
          margin-top: 2rem;
        }

        .achievement-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: #f8fafc;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }

        .achievement-icon {
          font-size: 2rem;
        }

        .achievement-content h4 {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e3a8a;
          margin: 0;
        }

        .achievement-content p {
          color: #6b7280;
          margin: 0;
          font-size: 0.9rem;
        }

        .university-showcase {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .showcase-card {
          padding: 1.5rem;
          border-radius: 15px;
          transition: transform 0.3s ease;
        }

        .showcase-card:hover {
          transform: translateX(10px);
        }

        .showcase-card.primary {
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          color: white;
        }

        .showcase-card.secondary {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
        }

        .showcase-card.tertiary {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
        }

        .card-icon {
          font-size: 2rem;
          margin-bottom: 1rem;
        }

        .showcase-card h3 {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .showcase-card p {
          opacity: 0.9;
          line-height: 1.6;
        }

        /* Process Section */
        .process-section {
          padding: 6rem 0;
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          color: white;
        }

        .process-section .section-badge {
          background: rgba(255, 255, 255, 0.2);
          color: white;
        }

        .process-section .section-title {
          color: white;
        }

        .process-section .section-description {
          color: rgba(255, 255, 255, 0.9);
        }

        .process-flow {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }

        /* Contact Section */
        .contact-section {
          padding: 6rem 0;
          background: #f8fafc;
        }

        .contact-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          margin-top: 3rem;
        }

        /* Enhanced Footer */
        .footer {
          background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%);
          color: white;
        }

        .footer-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        .footer-main {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 3rem;
          padding: 4rem 0 2rem;
        }

        .footer-logo {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .footer-logo .logo-circle {
          background: rgba(255, 255, 255, 0.2);
          position: relative;
          overflow: hidden;
        }

        .footer-logo-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          border-radius: 50%;
        }

        .footer-logo-fallback {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: white;
          font-weight: 700;
          font-size: 1.2rem;
        }

        .brand-info h3 {
          color: white;
          margin: 0;
          font-size: 1.2rem;
        }

        .brand-info p {
          color: rgba(255, 255, 255, 0.8);
          margin: 0;
          font-size: 0.9rem;
        }

        .footer-description {
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.7;
          margin-bottom: 2rem;
        }

        .social-links {
          display: flex;
          gap: 1rem;
        }

        .social-link {
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          text-decoration: none;
          transition: all 0.3s ease;
        }

        .social-link:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
        }

        .footer-links {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }

        .link-group h4 {
          color: white;
          font-size: 1.1rem;
          margin-bottom: 1rem;
        }

        .link-group ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .link-group li {
          margin-bottom: 0.5rem;
        }

        .link-group a {
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .link-group a:hover {
          color: white;
        }

        .footer-bottom {
          border-top: 1px solid rgba(255, 255, 255, 0.2);
          padding: 2rem 0;
        }

        .footer-bottom-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .footer-badges {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .footer-badge {
          background: rgba(255, 255, 255, 0.1);
          padding: 0.3rem 0.8rem;
          border-radius: 20px;
          font-size: 0.8rem;
        }

        /* Animations */
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .hero-container {
            grid-template-columns: 1fr;
            gap: 3rem;
            text-align: center;
          }

          .about-grid {
            grid-template-columns: 1fr;
            gap: 3rem;
          }

          .footer-main {
            grid-template-columns: 1fr;
            gap: 2rem;
          }
        }

        @media (max-width: 768px) {
          .nav-container {
            padding: 0 1rem;
          }

          .nav-menu {
            display: none; /* You might want to add a mobile menu */
          }

          .hero-container {
            padding: 0 1rem;
          }

          .hero-stats {
            flex-direction: column;
            gap: 1rem;
          }

          .hero-actions {
            flex-direction: column;
            align-items: center;
          }

          .hero-btn {
            width: 100%;
            max-width: 300px;
          }

          .features-grid {
            grid-template-columns: 1fr;
          }

          .achievement-grid {
            grid-template-columns: 1fr;
          }

          .process-flow {
            grid-template-columns: 1fr;
          }

          .contact-grid {
            grid-template-columns: 1fr;
          }

          .footer-links {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }

          .footer-bottom-content {
            flex-direction: column;
            text-align: center;
          }
        }

        @media (max-width: 480px) {
          .hero-title {
            font-size: 2rem;
          }

          .section-title {
            font-size: 1.8rem;
          }

          .certificate-frame {
            width: 280px;
            padding: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

// Enhanced Feature Card Component
const FeatureCard = ({ icon, title, description, color, delay }) => {
  const colorClasses = {
    blue: 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
    yellow: 'linear-gradient(135deg, #fef3c7, #fed7aa)',
    green: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
    purple: 'linear-gradient(135deg, #e9d5ff, #d8b4fe)',
    red: 'linear-gradient(135deg, #fee2e2, #fca5a5)',
    indigo: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)'
  };

  const iconColors = {
    blue: '#1e40af',
    yellow: '#d97706',
    green: '#059669',
    purple: '#7c3aed',
    red: '#dc2626',
    indigo: '#4338ca'
  };

  return (
    <div
      className="feature-card"
      style={{
        background: colorClasses[color],
        animationDelay: delay,
        animation: `fadeInUp 0.8s ease-out ${delay} both`
      }}
    >
      <div
        className="feature-icon"
        style={{ color: iconColors[color] }}
      >
        {icon}
      </div>
      <h3 className="feature-title">{title}</h3>
      <p className="feature-description">{description}</p>

      <style jsx>{`
        .feature-card {
          padding: 2rem;
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }

        .feature-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, ${iconColors[color]}, rgba(255,255,255,0.5));
        }

        .feature-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }

        .feature-icon {
          font-size: 3rem;
          margin-bottom: 1.5rem;
          display: block;
        }

        .feature-title {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1e3a8a;
          margin-bottom: 1rem;
        }

        .feature-description {
          color: #6b7280;
          line-height: 1.7;
          margin: 0;
        }
      `}</style>
    </div>
  );
};

// Process Step Component
const ProcessStep = ({ number, title, description, icon, color }) => {
  const colorClasses = {
    blue: '#3b82f6',
    green: '#10b981',
    purple: '#8b5cf6',
    orange: '#f59e0b'
  };

  return (
    <div className="process-step">
      <div
        className="step-number"
        style={{ background: colorClasses[color] }}
      >
        {number}
      </div>
      <div className="step-icon">{icon}</div>
      <h3 className="step-title">{title}</h3>
      <p className="step-description">{description}</p>

      <style jsx>{`
        .process-step {
          text-align: center;
          position: relative;
          padding: 2rem;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: transform 0.3s ease;
        }

        .process-step:hover {
          transform: translateY(-5px);
        }

        .step-number {
          position: absolute;
          top: -15px;
          left: 50%;
          transform: translateX(-50%);
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 0.9rem;
        }

        .step-icon {
          font-size: 3rem;
          margin: 1rem 0;
        }

        .step-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: white;
          margin-bottom: 1rem;
        }

        .step-description {
          color: rgba(255, 255, 255, 0.9);
          line-height: 1.6;
          margin: 0;
        }
      `}</style>
    </div>
  );
};

// Contact Card Component
const ContactCard = ({ icon, title, details, description, action }) => {
  return (
    <div className="contact-card">
      <div className="contact-icon">{icon}</div>
      <h3 className="contact-title">{title}</h3>
      <div className="contact-details">{details}</div>
      <p className="contact-description">{description}</p>
      <button className="contact-action">{action}</button>

      <style jsx>{`
        .contact-card {
          background: white;
          padding: 2rem;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          text-align: center;
          transition: all 0.3s ease;
          border: 1px solid #e2e8f0;
        }

        .contact-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .contact-icon {
          font-size: 3rem;
          margin-bottom: 1.5rem;
        }

        .contact-title {
          font-size: 1.3rem;
          font-weight: 600;
          color: #1e3a8a;
          margin-bottom: 1rem;
        }

        .contact-details {
          font-size: 1.1rem;
          font-weight: 600;
          color: #3b82f6;
          margin-bottom: 1rem;
        }

        .contact-description {
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 2rem;
        }

        .contact-action {
          background: linear-gradient(135deg, #1e3a8a, #3b82f6);
          color: white;
          border: none;
          padding: 0.8rem 2rem;
          border-radius: 10px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .contact-action:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 20px rgba(30, 58, 138, 0.3);
        }
      `}</style>
    </div>
  );
};

export default LandingPage;